import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  FiPlus, 
  FiTrash2, 
  FiSave, 
  FiEye,
  FiEyeOff,
  FiX,
  FiCheckCircle,
  FiCircle,
  FiArrowLeft,
  FiMenu,
  FiCheck,
  FiShare2,
  FiUsers,
} from 'react-icons/fi';
import { formsAPI, aiAPI } from '../services/api';
import ImageUpload from './ImageUpload';
import ShareModal from './ShareModal';
import UserShareModal from './UserShareModal';

const BuilderContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
  position: relative;
  background: white;
  color: black;
  min-height: 100vh;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Header = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 800;
  color: black;
  margin: 0;
  letter-spacing: -0.5px;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: ${props => props.variant === 'primary' ? 'black' : props.variant === 'danger' ? '#ef4444' : '#f3f4f6'};
  color: ${props => props.variant === 'primary' || props.variant === 'danger' ? 'white' : '#6b7280'};
  border: none;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${props => props.variant === 'primary' ? '#374151' : props.variant === 'danger' ? '#dc2626' : '#e5e7eb'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const FormSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px 0;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #374151;
  margin-bottom: 8px;
  font-size: 14px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: black;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.2s ease;
  box-sizing: border-box;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: black;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
`;

const QuestionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const QuestionCard = styled.div`
  background: ${props => props.isDragging ? '#f9fafb' : props.isHidden ? '#fef3c7' : 'white'};
  border: 2px solid ${props => props.isDragging ? 'black' : props.isHidden ? '#f59e0b' : '#e5e7eb'};
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s ease;
  opacity: ${props => props.isHidden ? 0.7 : 1};

  &:hover {
    border-color: ${props => props.isHidden ? '#d97706' : '#9ca3af'};
  }
`;

const QuestionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 16px;
`;

const QuestionNumber = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6b7280;
  font-weight: 600;
  font-size: 14px;
  min-width: 100px;
`;

const QuestionActions = styled.div`
  display: flex;
  gap: 8px;
`;

const IconButton = styled.button`
  padding: 8px;
  background: ${props => props.variant === 'danger' ? '#fef2f2' : '#f3f4f6'};
  color: ${props => props.variant === 'danger' ? '#ef4444' : '#6b7280'};
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.variant === 'danger' ? '#fee2e2' : '#e5e7eb'};
  }
`;

const QuestionContent = styled.div`
  flex: 1;
`;

const Select = styled.select`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  transition: all 0.2s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: black;
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
  }
`;

const OptionsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
`;

const OptionItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const OptionInput = styled.input`
  flex: 1;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  background: white;

  &:focus {
    outline: none;
    border-color: black;
  }
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  color: ${props => props.isCorrect ? '#059669' : '#6b7280'};
  font-weight: ${props => props.isCorrect ? '600' : '400'};
`;

const Checkbox = styled.input`
  cursor: pointer;
`;

const AddButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 10px;
  background: white;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  color: #6b7280;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: black;
    color: black;
    background: #f9fafb;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

function QuestionnaireBuilder({ formId: initialFormId }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [autoSaving, setAutoSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saving', 'saved', 'error'
  const [lastSaved, setLastSaved] = useState(null);
  const [currentFormId, setCurrentFormId] = useState(initialFormId);
  const autoSaveTimeoutRef = React.useRef(null);
  const lastAutoSaveRef = React.useRef(null); // Track last autosave to prevent duplicates
  const currentFormIdRef = React.useRef(initialFormId); // Keep ref in sync with state
  const isCreatingRef = React.useRef(false); // Track if we're currently creating a form
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    logo_url: '',
    password: '',
    formFields: [],
    questions: [],
    enable_certificate: false,
    certificate_title: '',
    certificate_template: 'default',
    enableRandomQuestions: false,
    questionsToShow: 10,
    enableTimer: false,
    timeLimitMinutes: 10
  });
  const [hasExistingPassword, setHasExistingPassword] = useState(false);
  const [passwordChanged, setPasswordChanged] = useState(false);
  const [shareModal, setShareModal] = useState({ isOpen: false, form: null });
  const [userShareModal, setUserShareModal] = useState({ isOpen: false, item: null });
  const [isConvertingWord, setIsConvertingWord] = useState(false);

  useEffect(() => {
    if (initialFormId) {
      console.log('Initializing with formId:', initialFormId);
      setCurrentFormId(initialFormId);
      currentFormIdRef.current = initialFormId;
      fetchForm();
    }
  }, [initialFormId]);

  // Autosave function with creation lock
  const autoSave = async () => {
    // Prevent simultaneous saves
    if (autoSaving || loading) {
      console.log('Skipping autosave - already saving or loading');
      return;
    }

    // Don't autosave if title is empty
    if (!formData.title.trim()) {
      console.log('Skipping autosave - no title');
      return;
    }

    // Time-based debounce check
    const now = Date.now();
    if (lastAutoSaveRef.current && (now - lastAutoSaveRef.current) < 3000) {
      console.log('Skipping autosave - too soon after last save');
      return;
    }

    try {
      setAutoSaving(true);
      setSaveStatus('saving');
      lastAutoSaveRef.current = now;

      const formStructure = {
        formFields: formData.formFields,
        questions: formData.questions,
        isQuestionnaire: true,
        totalPoints: formData.questions.reduce((sum, q) => sum + (q.points || 0), 0),
        enableRandomQuestions: formData.enableRandomQuestions || false,
        questionsToShow: formData.questionsToShow || 10,
        enableTimer: formData.enableTimer || false,
        timeLimitMinutes: formData.timeLimitMinutes || 10
      };

      // Only include password if it was changed or is a new password
      const payload = {
        title: formData.title,
        description: formData.description,
        logo_url: formData.logo_url,
        form_structure: formStructure,
        is_active: true,
        enable_certificate: formData.enable_certificate || false,
        certificate_title: formData.certificate_title || '',
        certificate_template: formData.certificate_template || 'default'
      };

      // Include password in payload if:
      // 1. For existing forms with password: only if password was changed
      // 2. For new forms or forms without password: if password is provided
      if (hasExistingPassword) {
        // If password was changed, send the new value. Otherwise, don't include it (undefined) to keep the existing password
        if (passwordChanged && formData.password && formData.password !== '••••••••' && formData.password.trim()) {
          payload.password = formData.password.trim();
          console.log('✅ Including changed password in payload (length:', payload.password.length, ')');
        } else {
          console.log('⏭️ Not including password - hasExistingPassword: true, passwordChanged:', passwordChanged, 'password:', formData.password ? `"${formData.password.substring(0, 3)}..." (${formData.password.length} chars)` : 'EMPTY');
        }
      } else {
        // New form or no existing password - send password if provided
        if (formData.password && formData.password.trim() && formData.password !== '••••••••') {
          payload.password = formData.password.trim();
          console.log('✅ Including new password in payload (length:', payload.password.length, ')');
        } else {
          console.log('⏭️ Not including password - hasExistingPassword: false, password:', formData.password ? `"${formData.password.substring(0, 3)}..." (${formData.password.length} chars)` : 'EMPTY', 'trimmed:', formData.password ? formData.password.trim() : 'N/A');
        }
      }
      
      console.log('📦 Final payload password field:', payload.password ? `PRESENT (${payload.password.length} chars)` : 'MISSING');

      // CRITICAL: Check if we have a form ID using ref (immediate, no render delay)
      const existingFormId = currentFormIdRef.current;

      if (existingFormId) {
        // UPDATE EXISTING FORM
        console.log('Autosave - updating existing questionnaire:', existingFormId);
        await formsAPI.update(existingFormId, payload);
        console.log('Successfully updated questionnaire');
      } else {
        // CREATE NEW FORM (with creation lock)
        
        // Check creation lock
        if (isCreatingRef.current) {
          console.log('Skipping autosave - creation already in progress');
          setAutoSaving(false);
          return;
        }

        // Set creation lock
        console.log('Autosave - creating new questionnaire (setting lock)');
        isCreatingRef.current = true;

        try {
          // Create form
          const response = await formsAPI.create(payload);
          const newFormId = response.data.data.id;
          console.log('Successfully created questionnaire:', newFormId);

          // Store form ID in ref (immediate access for next autosave)
          currentFormIdRef.current = newFormId;
          setCurrentFormId(newFormId);

          // Update URL without reload
          window.history.replaceState(null, '', `/questionnaire-builder/${newFormId}`);

          console.log('Form ID stored, future autosaves will UPDATE this questionnaire');
          toast.success('Questionnaire created successfully', { autoClose: 1500 });
        } finally {
          // Release creation lock
          isCreatingRef.current = false;
          console.log('Creation lock released');
        }
      }

      setSaveStatus('saved');
      setLastSaved(new Date());
    } catch (error) {
      console.error('Autosave error:', error);
      setSaveStatus('error');
      toast.error(`Autosave failed: ${error.response?.data?.error || error.message}`);
      
      // Release lock on error
      isCreatingRef.current = false;
    } finally {
      setAutoSaving(false);
    }
  };

  // Autosave effect with debounce
  useEffect(() => {
    // Don't autosave if title is empty
    if (!formData.title.trim()) {
      console.log('Skipping autosave trigger - no title');
      return;
    }

    console.log('Form data changed, setting autosave timeout');

    // Clear existing timeout
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }

    // Set new timeout (2 seconds after last change)
    autoSaveTimeoutRef.current = setTimeout(() => {
      console.log('Autosave timeout triggered');
      autoSave();
    }, 2000);

    // Cleanup
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
    };
  }, [formData, loading, autoSaving]);

  const fetchForm = async () => {
    try {
      setLoading(true);
      const response = await formsAPI.get(currentFormId);
      const form = response.data.data;
      
      console.log('Loaded questionnaire:', form);
      console.log('Form structure:', form.form_structure);
      console.log('Has password value:', form.has_password, 'Type:', typeof form.has_password);
      
      // Check if form has a password set (handle different types: 1, true, '1', etc.)
      const hasPassword = form.has_password === 1 || 
                         form.has_password === true || 
                         form.has_password === '1' ||
                         (typeof form.has_password === 'number' && form.has_password > 0) ||
                         (typeof form.has_password === 'boolean' && form.has_password === true);
      
      console.log('Has password result:', hasPassword);
      
      setHasExistingPassword(hasPassword);
      setPasswordChanged(false);
      
      setFormData({
        title: form.title || '',
        description: form.description || '',
        logo_url: form.logo_url || '',
        password: hasPassword ? '••••••••' : '', // Show dots if password exists
        enableMultiStep: form.enable_multi_step || false,
        formFields: Array.isArray(form.form_structure?.formFields) ? form.form_structure.formFields : [],
        questions: Array.isArray(form.form_structure?.questions) ? form.form_structure.questions : [],
        enable_certificate: form.enable_certificate || false,
        certificate_title: form.certificate_title || '',
        certificate_template: form.certificate_template || 'default',
        enableRandomQuestions: form.form_structure?.enableRandomQuestions || false,
        questionsToShow: form.form_structure?.questionsToShow || 10,
        enableTimer: form.form_structure?.enableTimer || false,
        timeLimitMinutes: form.form_structure?.timeLimitMinutes || 10
      });
      
      console.log('Set password field to:', hasPassword ? '••••••••' : '');
      
      console.log('Form data set:', {
        formFields: Array.isArray(form.form_structure?.formFields) ? form.form_structure.formFields.length : 0,
        questions: Array.isArray(form.form_structure?.questions) ? form.form_structure.questions.length : 0
      });
    } catch (error) {
      toast.error('Failed to load questionnaire');
      console.error('Load form error:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const handleAddFormField = () => {
    const newField = {
      id: `field_${Date.now()}`,
      name: `field_${Date.now()}`,
      label: '',
      type: 'text',
      required: false,
      width: 'full' // 'full', 'half', or 'third'
    };

    setFormData(prev => ({
      ...prev,
      formFields: [...prev.formFields, newField]
    }));
  };

  const handleUpdateFormField = (fieldId, field, value) => {
    setFormData(prev => ({
      ...prev,
      formFields: prev.formFields.map(f =>
        f.id === fieldId ? { ...f, [field]: value } : f
      )
    }));
  };

  const handleDeleteFormField = (fieldId) => {
    if (window.confirm('Are you sure you want to delete this field?')) {
      setFormData(prev => ({
        ...prev,
        formFields: prev.formFields.filter(f => f.id !== fieldId)
      }));
    }
  };

  const handleAddQuestion = () => {
    try {
      const timestamp = Date.now();
      const newQuestion = {
        id: `q_${timestamp}`,
        type: 'multiple_choice',
        question: '',
        options: [
          { id: `opt_${timestamp}_1`, text: '', isCorrect: false },
          { id: `opt_${timestamp}_2`, text: '', isCorrect: false }
        ],
        points: 1,
        required: true,
        visible: true // Questions are visible by default
      };

      console.log('Adding new question:', newQuestion);

      setFormData(prev => {
        const currentQuestions = Array.isArray(prev.questions) ? prev.questions : [];
        console.log('Current questions:', currentQuestions.length);
        
        return {
          ...prev,
          questions: [...currentQuestions, newQuestion]
        };
      });
    } catch (error) {
      console.error('Error adding question:', error);
      toast.error('Failed to add question');
    }
  };

  const handleUpdateQuestion = (questionId, field, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId ? { ...q, [field]: value } : q
      )
    }));
  };

  const handleDeleteQuestion = (questionId) => {
    if (window.confirm('Are you sure you want to delete this question?')) {
      setFormData(prev => ({
        ...prev,
        questions: prev.questions.filter(q => q.id !== questionId)
      }));
    }
  };

  const handleAddOption = (questionId) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => {
        if (q.id === questionId) {
          return {
            ...q,
            options: [
              ...q.options,
              { id: `opt_${Date.now()}`, text: '', isCorrect: false }
            ]
          };
        }
        return q;
      })
    }));
  };

  const handleUpdateOption = (questionId, optionId, field, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => {
        if (q.id === questionId) {
          return {
            ...q,
            options: q.options.map(opt => 
              opt.id === optionId ? { ...opt, [field]: value } : opt
            )
          };
        }
        return q;
      })
    }));
  };

  const handleDeleteOption = (questionId, optionId) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => {
        if (q.id === questionId && q.options.length > 2) {
          return {
            ...q,
            options: q.options.filter(opt => opt.id !== optionId)
          };
        }
        return q;
      })
    }));
  };

  const handleToggleCorrectAnswer = (questionId, optionId) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => {
        if (q.id === questionId) {
          if (q.type === 'multiple_choice' || q.type === 'true_false') {
            // Single correct answer
            return {
              ...q,
              options: q.options.map(opt => ({
                ...opt,
                isCorrect: opt.id === optionId
              }))
            };
          } else if (q.type === 'checkbox') {
            // Multiple correct answers
            return {
              ...q,
              options: q.options.map(opt => 
                opt.id === optionId ? { ...opt, isCorrect: !opt.isCorrect } : opt
              )
            };
          }
        }
        return q;
      })
    }));
  };

  const handleToggleQuestionVisibility = (questionId) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map(q => 
        q.id === questionId ? { ...q, visible: q.visible === false ? true : false } : q
      )
    }));
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(formData.questions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setFormData(prev => ({ ...prev, questions: items }));
  };

  const handleWordUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type (only .docx is supported, not .doc)
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!validTypes.includes(file.type) && fileExtension !== '.docx') {
      toast.error('Please upload a .docx file. Legacy .doc files are not supported. Please convert to .docx format.');
      return;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setIsConvertingWord(true);

    try {
      const response = await aiAPI.convertWordToQuestionnaire(file);
      
      if (response.data.success && response.data.questionnaire) {
        const questionnaire = response.data.questionnaire;
        
        // Update title and description if provided
        if (questionnaire.title) {
          setFormData(prev => ({ ...prev, title: questionnaire.title }));
        }
        if (questionnaire.description) {
          setFormData(prev => ({ ...prev, description: questionnaire.description }));
        }

        // Add imported questions to existing questions
        if (questionnaire.questions && questionnaire.questions.length > 0) {
          const importedQuestions = questionnaire.questions.map((q, index) => ({
            ...q,
            id: q.id || `q_imported_${Date.now()}_${index}`,
            options: q.options.map((opt, optIndex) => ({
              ...opt,
              id: opt.id || `opt_${q.id || `q_imported_${Date.now()}_${index}`}_${optIndex}`
            }))
          }));

          setFormData(prev => ({
            ...prev,
            questions: [...prev.questions, ...importedQuestions]
          }));

          toast.success(`Successfully imported ${importedQuestions.length} question(s) from Word document!`);
        } else {
          toast.warning('No questions found in the Word document');
        }
      } else {
        throw new Error(response.data.error || 'Failed to convert Word document');
      }
    } catch (error) {
      console.error('Error converting Word to questionnaire:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to convert Word document. Please try again.');
    } finally {
      setIsConvertingWord(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const validateForm = (isAutoSave = false) => {
    // Title is always required
    if (!formData.title.trim()) {
      if (!isAutoSave) toast.error('Please enter a questionnaire title');
      return false;
    }

    // For autosave, only check title
    if (isAutoSave) {
      return true;
    }

    // For manual save, validate only if fields/questions exist
    // This allows saving with just basic information
    
    // Validate form fields (only if any exist)
    for (let i = 0; i < formData.formFields.length; i++) {
      const field = formData.formFields[i];
      
      if (!field.label.trim()) {
        toast.error(`Form field ${i + 1} is missing a name`);
        return false;
      }
    }

    // Validate questions (only if any exist)
    for (let i = 0; i < formData.questions.length; i++) {
      const q = formData.questions[i];
      
      if (!q.question.trim()) {
        toast.error(`Question ${i + 1} is empty`);
        return false;
      }

      if (q.type !== 'short_answer' && q.type !== 'long_answer') {
        const hasCorrectAnswer = q.options.some(opt => opt.isCorrect);
        if (!hasCorrectAnswer) {
          toast.error(`Question ${i + 1} must have at least one correct answer`);
          return false;
        }

        const hasEmptyOption = q.options.some(opt => !opt.text.trim());
        if (hasEmptyOption) {
          toast.error(`Question ${i + 1} has empty options`);
          return false;
        }
      }
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    // Check if we have a form ID using ref (immediate access)
    const existingFormId = currentFormIdRef.current;

    try {
      setLoading(true);
      setSaveStatus('saving');

      const formStructure = {
        formFields: formData.formFields,
        questions: formData.questions,
        isQuestionnaire: true,
        totalPoints: formData.questions.reduce((sum, q) => sum + (q.points || 0), 0),
        enableRandomQuestions: formData.enableRandomQuestions || false,
        questionsToShow: formData.questionsToShow || 10,
        enableTimer: formData.enableTimer || false,
        timeLimitMinutes: formData.timeLimitMinutes || 10
      };

      // Only include password if it was changed or is a new password
      const payload = {
        title: formData.title,
        description: formData.description,
        logo_url: formData.logo_url,
        form_structure: formStructure,
        is_active: true,
        is_template: false, // Always save as personal form, not template
        enable_multi_step: formData.enableMultiStep || false,
        enable_certificate: formData.enable_certificate || false,
        certificate_title: formData.certificate_title || '',
        certificate_template: formData.certificate_template || 'default'
      };

      // Include password in payload if:
      // 1. For existing forms with password: only if password was changed
      // 2. For new forms or forms without password: if password is provided
      if (hasExistingPassword) {
        // If password was changed, send the new value. Otherwise, don't include it (undefined) to keep the existing password
        if (passwordChanged && formData.password && formData.password !== '••••••••' && formData.password.trim()) {
          payload.password = formData.password.trim();
          console.log('✅ Including changed password in payload (length:', payload.password.length, ')');
        } else {
          console.log('⏭️ Not including password - hasExistingPassword: true, passwordChanged:', passwordChanged, 'password:', formData.password ? `"${formData.password.substring(0, 3)}..." (${formData.password.length} chars)` : 'EMPTY');
        }
      } else {
        // New form or no existing password - send password if provided
        if (formData.password && formData.password.trim() && formData.password !== '••••••••') {
          payload.password = formData.password.trim();
          console.log('✅ Including new password in payload (length:', payload.password.length, ')');
        } else {
          console.log('⏭️ Not including password - hasExistingPassword: false, password:', formData.password ? `"${formData.password.substring(0, 3)}..." (${formData.password.length} chars)` : 'EMPTY', 'trimmed:', formData.password ? formData.password.trim() : 'N/A');
        }
      }
      
      console.log('📦 Final payload password field:', payload.password ? `PRESENT (${payload.password.length} chars)` : 'MISSING');

      console.log('Manual save payload:', JSON.stringify(payload, null, 2));

      if (existingFormId) {
        // UPDATE EXISTING QUESTIONNAIRE
        console.log('Updating existing questionnaire:', existingFormId);
        await formsAPI.update(existingFormId, payload);
        setSaveStatus('saved');
        setLastSaved(new Date());
        toast.success('Questionnaire updated successfully');
        
        // Navigate back to dashboard after update
        navigate('/');
      } else {
        // CREATE NEW QUESTIONNAIRE (with creation lock)
        
        // Check if creation is already in progress
        if (isCreatingRef.current) {
          toast.info('Save in progress, please wait...');
          setLoading(false);
          return;
        }

        console.log('Creating new questionnaire');
        isCreatingRef.current = true;

        try {
          const response = await formsAPI.create(payload);
          const newFormId = response.data.data.id;
          console.log('Created questionnaire with ID:', newFormId);
          
          // Update both state and ref immediately
          setCurrentFormId(newFormId);
          currentFormIdRef.current = newFormId;
          window.history.replaceState(null, '', `/questionnaire-builder/${newFormId}`);
          
          setSaveStatus('saved');
          setLastSaved(new Date());
          toast.success('Questionnaire created successfully');
          
          // Stay on page for new forms so user can continue editing
          console.log('Form created successfully, staying on page for further editing');
        } finally {
          isCreatingRef.current = false;
        }
      }
    } catch (error) {
      setSaveStatus('error');
      console.error('Save questionnaire error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error(`Failed to save: ${error.response?.data?.error || error.message}`);
      isCreatingRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = () => {
    // Save to localStorage for preview
    localStorage.setItem('previewForm', JSON.stringify({
      title: formData.title,
      description: formData.description,
      logo_url: formData.logo_url,
      form_structure: {
        formFields: formData.formFields,
        questions: formData.questions,
        isQuestionnaire: true,
        totalPoints: formData.questions.reduce((sum, q) => sum + (q.points || 0), 0),
        enableTimer: formData.enableTimer || false,
        timeLimitMinutes: formData.timeLimitMinutes || 10
      },
      enable_multi_step: formData.enableMultiStep || false,
      is_active: true
    }));
    window.open('/preview/preview', '_blank');
  };

  const openShareModal = () => {
    const existingFormId = currentFormIdRef.current;
    if (!existingFormId) {
      toast.error('Please save the questionnaire first before sharing');
      return;
    }
    setShareModal({ 
      isOpen: true, 
      form: {
        id: existingFormId,
        title: formData.title,
        description: formData.description,
        custom_url: formData.custom_url
      }
    });
  };

  const closeShareModal = () => {
    setShareModal({ isOpen: false, form: null });
  };

  const openUserShareModal = () => {
    const existingFormId = currentFormIdRef.current;
    if (!existingFormId) {
      toast.error('Please save the questionnaire first before sharing');
      return;
    }
    setUserShareModal({ 
      isOpen: true, 
      item: {
        id: existingFormId,
        title: formData.title,
        description: formData.description
      }
    });
  };

  const closeUserShareModal = () => {
    setUserShareModal({ isOpen: false, item: null });
  };

  if (loading) {
    return (
      <BuilderContainer>
        <div style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading...</div>
        </div>
      </BuilderContainer>
    );
  }

  return (
    <BuilderContainer>
      <Header>
        <HeaderTop>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <Title>🎯 Questionnaire Builder</Title>
            {formData.title && (
              <div style={{ 
                fontSize: '13px', 
                color: saveStatus === 'saving' ? '#3b82f6' : saveStatus === 'saved' ? '#059669' : '#ef4444',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                fontWeight: '500'
              }}>
                {saveStatus === 'saving' && (
                  <>
                    <div style={{ 
                      width: '12px', 
                      height: '12px', 
                      border: '2px solid #3b82f6',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }} />
                    Saving...
                  </>
                )}
                {saveStatus === 'saved' && (
                  <>
                    <FiCheck />
                    Saved {lastSaved && `at ${lastSaved.toLocaleTimeString()}`}
                  </>
                )}
                {saveStatus === 'error' && (
                  <>
                    <FiX />
                    Save failed
                  </>
                )}
              </div>
            )}
          </div>
          <ButtonGroup>
            <Button onClick={() => navigate('/')}>
              <FiArrowLeft />
              Back
            </Button>
            <Button onClick={handlePreview}>
              <FiEye />
              Preview
            </Button>
            <Button onClick={openShareModal}>
              <FiShare2 />
              Share Publicly
            </Button>
            <Button onClick={openUserShareModal}>
              <FiUsers />
              Share with Users
            </Button>
            <Button variant="primary" onClick={handleSave} disabled={loading || autoSaving}>
              <FiSave />
              {currentFormId ? 'Update' : 'Save'} Questionnaire
            </Button>
          </ButtonGroup>
        </HeaderTop>
      </Header>

      <FormSection>
        <SectionTitle>Basic Information</SectionTitle>
        
        <FormGroup>
          <Label>Title *</Label>
          <Input
            type="text"
            placeholder="Enter questionnaire title..."
            value={formData.title}
            onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          />
        </FormGroup>

        <FormGroup>
          <Label>Description</Label>
          <TextArea
            placeholder="Enter questionnaire description (optional)..."
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          />
        </FormGroup>

        <FormGroup>
          <Label>Logo</Label>
          <ImageUpload
            value={formData.logo_url}
            onChange={(url) => setFormData(prev => ({ ...prev, logo_url: url }))}
          />
        </FormGroup>

        <FormGroup>
          <Label>Password (Optional)</Label>
          <Input
            type="password"
            placeholder={hasExistingPassword ? "Enter new password or leave unchanged..." : "Enter password to protect questionnaire (optional)..."}
            value={formData.password || ''}
            onChange={(e) => {
              const newPassword = e.target.value;
              setFormData(prev => ({ ...prev, password: newPassword }));
              // Check if password was changed (not the placeholder dots)
              if (hasExistingPassword) {
                // If user is typing a new password (not dots), mark as changed
                const isChanged = newPassword !== '••••••••' && newPassword !== '' && newPassword.trim() !== '';
                setPasswordChanged(isChanged);
                console.log('Password onChange - hasExistingPassword: true, newPassword length:', newPassword.length, 'isChanged:', isChanged);
              } else {
                // For new forms, any non-empty password is a change
                const isChanged = newPassword !== '' && newPassword.trim() !== '';
                setPasswordChanged(isChanged);
                console.log('Password onChange - hasExistingPassword: false, newPassword length:', newPassword.length, 'isChanged:', isChanged);
              }
            }}
            onFocus={(e) => {
              // Clear the placeholder dots when user focuses on the field
              if (hasExistingPassword && !passwordChanged && formData.password === '••••••••') {
                console.log('Password onFocus - clearing dots placeholder');
                e.target.value = '';
                setFormData(prev => ({ ...prev, password: '' }));
                setPasswordChanged(false);
              }
            }}
            onBlur={(e) => {
              // Restore dots if field is empty and password wasn't changed (only for existing passwords)
              if (hasExistingPassword && !passwordChanged && (!formData.password || formData.password === '' || formData.password.trim() === '')) {
                console.log('Password onBlur - restoring dots because password not changed');
                setFormData(prev => ({ ...prev, password: '••••••••' }));
              }
            }}
          />
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            {hasExistingPassword ? (
              'Password is set. Enter a new password to change it, or leave unchanged to keep the current password.'
            ) : (
              'Users will need to enter this password to access the questionnaire'
            )}
          </div>
          {/* Debug info - remove in production */}
          {process.env.NODE_ENV === 'development' && (
            <div style={{ fontSize: '10px', color: '#9ca3af', marginTop: '4px' }}>
              Debug: hasExistingPassword={String(hasExistingPassword)}, password={formData.password ? '[' + formData.password.length + ' chars]' : 'empty'}, passwordChanged={String(passwordChanged)}
            </div>
          )}
        </FormGroup>

        {/* Multi-Step Option */}
        <FormGroup>
          <div style={{ 
            padding: '16px', 
            background: '#f0f9ff', 
            borderRadius: '8px', 
            border: '1px solid #bae6fd'
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.enableMultiStep || false}
                onChange={(e) => setFormData(prev => ({ ...prev, enableMultiStep: e.target.checked }))}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#0c4a6e' }}>
                  Enable Multi-Step Questionnaire
                </div>
                <div style={{ fontSize: '12px', color: '#075985', marginTop: '4px' }}>
                  Show each question as a separate step with Next/Previous buttons
                </div>
              </div>
            </label>
          </div>
        </FormGroup>
      </FormSection>

      <FormSection>
        <SectionTitle>Certificate Settings</SectionTitle>
        
        <FormGroup>
          <div style={{ 
            padding: '16px', 
            background: '#f0f9ff', 
            borderRadius: '8px', 
            border: '1px solid #bae6fd',
            marginBottom: '20px'
          }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.enable_certificate || false}
                onChange={(e) => setFormData(prev => ({ ...prev, enable_certificate: e.target.checked }))}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <div>
                <div style={{ fontSize: '14px', fontWeight: '600', color: '#0c4a6e' }}>
                  Enable Certificate Generation
                </div>
                <div style={{ fontSize: '12px', color: '#075985', marginTop: '4px' }}>
                  Show certificate download option after questionnaire completion
                </div>
              </div>
            </label>
          </div>
        </FormGroup>

        {formData.enable_certificate && (
          <>
            <FormGroup>
              <Label>Certificate Title *</Label>
              <Input
                type="text"
                placeholder="e.g., BASIC FOOD SAFETY AND HYGIENE TRAINING"
                value={formData.certificate_title}
                onChange={(e) => setFormData(prev => ({ ...prev, certificate_title: e.target.value }))}
              />
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                This title will appear on the certificate
              </div>
            </FormGroup>

            <FormGroup>
              <Label>Certificate Template</Label>
              <Select
                value={formData.certificate_template || 'default'}
                onChange={(e) => setFormData(prev => ({ ...prev, certificate_template: e.target.value }))}
              >
                <option value="default">Default Template</option>
              </Select>
              <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                Choose a certificate template (more templates coming soon)
              </div>
            </FormGroup>
          </>
        )}
      </FormSection>

      <FormSection>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <SectionTitle style={{ margin: 0 }}>
            Question Bank Settings
          </SectionTitle>
        </div>
        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '20px' }}>
          Enable random question selection to show different questions to each user from your question bank.
        </p>
        
        <FormGroup>
          <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              checked={formData.enableRandomQuestions}
              onChange={(e) => setFormData(prev => ({ ...prev, enableRandomQuestions: e.target.checked }))}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            Enable Random Question Selection
          </Label>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            When enabled, each user will see a random subset of questions from your question bank
          </div>
        </FormGroup>

        {formData.enableRandomQuestions && (
          <FormGroup>
            <Label>Number of Questions to Show</Label>
            <input
              type="number"
              min="1"
              max={formData.questions.length || 1}
              value={formData.questionsToShow}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 1;
                const maxQuestions = formData.questions.length || 1;
                setFormData(prev => ({ 
                  ...prev, 
                  questionsToShow: Math.min(Math.max(1, value), maxQuestions) 
                }));
              }}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              Show {formData.questionsToShow} question{formData.questionsToShow !== 1 ? 's' : ''} from your bank of {formData.questions.length} question{formData.questions.length !== 1 ? 's' : ''}
            </div>
          </FormGroup>
        )}
      </FormSection>

      <FormSection>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <SectionTitle style={{ margin: 0 }}>
            Timer Settings
          </SectionTitle>
        </div>
        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '20px' }}>
          Turn this quiz into a timed assessment. A countdown is shown while the user answers, and the quiz auto-submits when time runs out.
        </p>

        <FormGroup>
          <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              type="checkbox"
              checked={formData.enableTimer}
              onChange={(e) => setFormData(prev => ({ ...prev, enableTimer: e.target.checked }))}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            Enable Timer
          </Label>
          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
            When enabled, the score is also shown to the user immediately after they submit
          </div>
        </FormGroup>

        {formData.enableTimer && (
          <FormGroup>
            <Label>Time Limit (minutes)</Label>
            <input
              type="number"
              min="1"
              max="180"
              value={formData.timeLimitMinutes}
              onChange={(e) => {
                const value = parseInt(e.target.value) || 1;
                setFormData(prev => ({ ...prev, timeLimitMinutes: Math.min(Math.max(1, value), 180) }));
              }}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
              Users get {formData.timeLimitMinutes} minute{formData.timeLimitMinutes !== 1 ? 's' : ''} to complete the quiz
            </div>
          </FormGroup>
        )}
      </FormSection>

      <FormSection>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <SectionTitle style={{ margin: 0 }}>
            Form Fields ({formData.formFields.length})
          </SectionTitle>
        </div>
        <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '20px' }}>
          Add fields to collect user information before they take the quiz (e.g., Employee Number, Full Name, Email, etc.)
        </p>

        {formData.formFields.length === 0 ? (
          <EmptyState>
            <p style={{ fontSize: '16px', marginBottom: '20px' }}>
              No form fields yet. Add fields to collect user information (optional).
            </p>
            <Button variant="primary" onClick={handleAddFormField}>
              <FiPlus />
              Add Form Field
            </Button>
          </EmptyState>
        ) : (
          <>
            <QuestionsList>
              {formData.formFields.map((field, index) => (
                <QuestionCard key={field.id}>
                  <QuestionHeader>
                    <QuestionNumber>
                      Field {index + 1}
                    </QuestionNumber>
                    <QuestionActions>
                      <IconButton
                        variant="danger"
                        onClick={() => handleDeleteFormField(field.id)}
                      >
                        <FiTrash2 />
                      </IconButton>
                    </QuestionActions>
                  </QuestionHeader>

                  <QuestionContent>
                    <Row>
                      <FormGroup>
                        <Label>Field Type</Label>
                        <Select
                          value={field.type}
                          onChange={(e) => handleUpdateFormField(field.id, 'type', e.target.value)}
                        >
                          <option value="text">Text</option>
                          <option value="email">Email</option>
                          <option value="number">Number</option>
                          <option value="tel">Phone</option>
                          <option value="date">Date</option>
                        </Select>
                      </FormGroup>

                      <FormGroup>
                        <Label>Field Width</Label>
                        <Select
                          value={field.width || 'full'}
                          onChange={(e) => handleUpdateFormField(field.id, 'width', e.target.value)}
                        >
                          <option value="full">Full Width (100%)</option>
                          <option value="half">Half Width (50%)</option>
                          <option value="third">Third Width (33%)</option>
                        </Select>
                      </FormGroup>
                    </Row>

                    <Row>
                      <FormGroup>
                        <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Checkbox
                            type="checkbox"
                            checked={field.required}
                            onChange={(e) => handleUpdateFormField(field.id, 'required', e.target.checked)}
                          />
                          Required
                        </Label>
                      </FormGroup>
                      <FormGroup>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>
                          💡 Use "Half" or "Third" to place fields side by side
                        </div>
                      </FormGroup>
                    </Row>

                    <FormGroup>
                      <Label>Field Name *</Label>
                      <Input
                        type="text"
                        placeholder="e.g., Employee Number, Full Name, Email Address, etc."
                        value={field.label}
                        onChange={(e) => handleUpdateFormField(field.id, 'label', e.target.value)}
                      />
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        This will be shown as the field label and placeholder
                      </div>
                    </FormGroup>
                  </QuestionContent>
                </QuestionCard>
              ))}
            </QuestionsList>

            <AddButton onClick={handleAddFormField} style={{ marginTop: '16px' }}>
              <FiPlus />
              Add Another Field
            </AddButton>
          </>
        )}
      </FormSection>

      <FormSection>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <SectionTitle style={{ margin: 0 }}>
            Questions ({formData.questions.length}) - Visible: {formData.questions.filter(q => q.visible !== false).length}
          </SectionTitle>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '600' }}>
              Total Points: {formData.questions.filter(q => q.visible !== false).reduce((sum, q) => sum + (q.points || 0), 0)}
            </div>
            <input
              type="file"
              id="word-file-input"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={handleWordUpload}
              disabled={isConvertingWord}
              style={{ display: 'none' }}
            />
            <Button
              variant="primary"
              disabled={isConvertingWord}
              onClick={() => {
                if (!isConvertingWord) {
                  document.getElementById('word-file-input')?.click();
                }
              }}
              style={{ opacity: isConvertingWord ? 0.6 : 1, cursor: isConvertingWord ? 'not-allowed' : 'pointer' }}
            >
              {isConvertingWord ? (
                <>
                  <div style={{ 
                    width: '14px', 
                    height: '14px', 
                    border: '2px solid white',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                    marginRight: '8px'
                  }} />
                  Converting...
                </>
              ) : (
                <>
                  <FiPlus />
                  Import from Word
                </>
              )}
            </Button>
          </div>
        </div>
        {isConvertingWord && (
          <div style={{ 
            padding: '12px', 
            background: '#f0f9ff', 
            borderRadius: '8px', 
            border: '1px solid #bae6fd',
            marginBottom: '20px',
            fontSize: '14px',
            color: '#0c4a6e'
          }}>
            Processing Word document with AI... This may take a moment.
          </div>
        )}

        {formData.questions.length === 0 ? (
          <EmptyState>
            <p style={{ fontSize: '16px', marginBottom: '20px' }}>
              No questions yet. Add your first question to get started!
            </p>
            <Button variant="primary" onClick={handleAddQuestion}>
              <FiPlus />
              Add First Question
            </Button>
          </EmptyState>
        ) : (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="questions">
              {(provided) => (
                <QuestionsList {...provided.droppableProps} ref={provided.innerRef}>
                  {formData.questions.map((question, index) => (
                    <Draggable key={question.id} draggableId={question.id} index={index}>
                      {(provided, snapshot) => (
                        <QuestionCard
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          isDragging={snapshot.isDragging}
                          isHidden={question.visible === false}
                        >
                          <QuestionHeader>
                            <QuestionNumber {...provided.dragHandleProps}>
                              <FiMenu />
                              Question {index + 1}
                              {question.visible === false && (
                                <span style={{ 
                                  fontSize: '12px', 
                                  color: '#d97706', 
                                  fontWeight: '500',
                                  marginLeft: '8px'
                                }}>
                                  (Hidden)
                                </span>
                              )}
                            </QuestionNumber>
                            <QuestionActions>
                              <IconButton
                                variant={question.visible === false ? 'danger' : 'default'}
                                onClick={() => handleToggleQuestionVisibility(question.id)}
                                title={question.visible === false ? 'Show question' : 'Hide question'}
                              >
                                {question.visible === false ? <FiEyeOff /> : <FiEye />}
                              </IconButton>
                              <IconButton
                                variant="danger"
                                onClick={() => handleDeleteQuestion(question.id)}
                              >
                                <FiTrash2 />
                              </IconButton>
                            </QuestionActions>
                          </QuestionHeader>

                          <QuestionContent>
                            <Row>
                              <FormGroup>
                                <Label>Question Type</Label>
                                <Select
                                  value={question.type}
                                  onChange={(e) => {
                                    const newType = e.target.value;
                                    handleUpdateQuestion(question.id, 'type', newType);
                                    
                                    // Adjust options based on type
                                    if (newType === 'true_false') {
                                      handleUpdateQuestion(question.id, 'options', [
                                        { id: `opt_${Date.now()}_1`, text: 'True', isCorrect: false },
                                        { id: `opt_${Date.now()}_2`, text: 'False', isCorrect: false }
                                      ]);
                                    }
                                  }}
                                >
                                  <option value="multiple_choice">Multiple Choice</option>
                                  <option value="checkbox">Multiple Select</option>
                                  <option value="true_false">True/False</option>
                                  <option value="short_answer">Short Answer</option>
                                </Select>
                              </FormGroup>

                              <FormGroup>
                                <Label>Points</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={question.points || 1}
                                  onChange={(e) => handleUpdateQuestion(question.id, 'points', parseInt(e.target.value) || 1)}
                                />
                              </FormGroup>
                            </Row>

                            <FormGroup>
                              <Label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Checkbox
                                  type="checkbox"
                                  checked={question.required !== false}
                                  onChange={(e) => handleUpdateQuestion(question.id, 'required', e.target.checked)}
                                />
                                Required
                              </Label>
                            </FormGroup>

                            <FormGroup>
                              <Label>Question Text *</Label>
                              <TextArea
                                placeholder="Enter your question..."
                                value={question.question}
                                onChange={(e) => handleUpdateQuestion(question.id, 'question', e.target.value)}
                              />
                            </FormGroup>

                            {(question.type === 'multiple_choice' || question.type === 'checkbox' || question.type === 'true_false') && (
                              <>
                                <Label>
                                  Answer Options 
                                  {question.type === 'checkbox' 
                                    ? ' (Select all correct answers)' 
                                    : ' (Select the correct answer)'}
                                </Label>
                                <OptionsList>
                                  {question.options.map((option, optIndex) => (
                                    <OptionItem key={option.id}>
                                      <CheckboxLabel isCorrect={option.isCorrect}>
                                        <Checkbox
                                          type={question.type === 'checkbox' ? 'checkbox' : 'radio'}
                                          checked={option.isCorrect}
                                          onChange={() => handleToggleCorrectAnswer(question.id, option.id)}
                                        />
                                        {option.isCorrect && <FiCheckCircle style={{ color: '#059669' }} />}
                                      </CheckboxLabel>
                                      
                                      <OptionInput
                                        type="text"
                                        placeholder={`Option ${optIndex + 1}`}
                                        value={option.text}
                                        onChange={(e) => handleUpdateOption(question.id, option.id, 'text', e.target.value)}
                                        disabled={question.type === 'true_false'}
                                      />
                                      
                                      {question.type !== 'true_false' && question.options.length > 2 && (
                                        <IconButton
                                          variant="danger"
                                          onClick={() => handleDeleteOption(question.id, option.id)}
                                        >
                                          <FiX />
                                        </IconButton>
                                      )}
                                    </OptionItem>
                                  ))}
                                </OptionsList>

                                {question.type !== 'true_false' && (
                                  <AddButton
                                    onClick={() => handleAddOption(question.id)}
                                    style={{ marginTop: '12px' }}
                                  >
                                    <FiPlus />
                                    Add Option
                                  </AddButton>
                                )}
                              </>
                            )}

                            {question.type === 'short_answer' && (
                              <div style={{ 
                                marginTop: '12px', 
                                padding: '12px', 
                                background: '#fef3c7', 
                                borderRadius: '8px',
                                fontSize: '13px',
                                color: '#92400e'
                              }}>
                                <strong>Note:</strong> Short answer questions will require manual grading.
                              </div>
                            )}
                          </QuestionContent>
                        </QuestionCard>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </QuestionsList>
              )}
            </Droppable>
          </DragDropContext>
        )}

        {formData.questions.length > 0 && (
          <AddButton onClick={handleAddQuestion} style={{ marginTop: '16px' }}>
            <FiPlus />
            Add Question
          </AddButton>
        )}
      </FormSection>

      {/* Bottom Action Buttons */}
      <div style={{ 
        position: 'sticky',
        bottom: '20px',
        background: 'white',
        padding: '20px',
        borderRadius: '12px',
        boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)',
        border: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '16px'
      }}>
        <div style={{ fontSize: '14px', color: '#6b7280' }}>
          {!currentFormId && !lastSaved && <>⏳ Autosave enabled - will create on first change</>}
          {saveStatus === 'saving' && <>🔄 Saving...</>}
          {saveStatus === 'saved' && lastSaved && (
            <>✅ Saved at {lastSaved.toLocaleTimeString()}</>
          )}
          {saveStatus === 'error' && <>❌ Save failed - please try again</>}
        </div>
        <ButtonGroup>
          <Button onClick={handlePreview}>
            <FiEye />
            Preview
          </Button>
          <Button onClick={openShareModal}>
            <FiShare2 />
            Share Publicly
          </Button>
          <Button onClick={openUserShareModal}>
            <FiUsers />
            Share with Users
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={loading || autoSaving}>
            <FiSave />
            {currentFormId ? 'Update' : 'Save'} Questionnaire
          </Button>
        </ButtonGroup>
      </div>

      <ShareModal
        isOpen={shareModal.isOpen}
        onClose={closeShareModal}
        form={shareModal.form}
        routeFormId={currentFormId || initialFormId}
      />

      <UserShareModal
        isOpen={userShareModal.isOpen}
        onClose={closeUserShareModal}
        item={userShareModal.item}
        type="form"
      />
    </BuilderContainer>
  );
}

export default QuestionnaireBuilder;

